import axios from 'axios';

/**
 * Persona Service
 *
 * Handles all persona-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

/**
 * Persona interface matching backend
 */
export interface Persona {
  id: string;
  project_id: string;
  name: string;
  role?: string | null;
  description?: string | null;
  goals?: string | null;
  pain_points?: string | null;
  avatar_id: string;
  age_range?: string | null;
  gender?: string | null;
  location?: string | null;
  created_by: string;
  created_at: string;
  updated_at?: string | null;
  is_active: boolean;
}

/**
 * Input interface for creating a new persona
 */
export interface CreatePersonaInput {
  name: string;
  role?: string;
  description?: string;
  goals?: string;
  pain_points?: string;
  avatar_id: string;
  age_range?: string;
  gender?: string;
  location?: string;
}

/**
 * Input interface for updating a persona
 */
export interface UpdatePersonaInput {
  name?: string;
  role?: string | null;
  description?: string | null;
  goals?: string | null;
  pain_points?: string | null;
  avatar_id?: string;
  age_range?: string | null;
  gender?: string | null;
  location?: string | null;
}

/**
 * Get all personas for a project
 */
export async function getPersonas(projectId: string, token: string): Promise<Persona[]> {
  const response = await axios.get(
    `${API_BASE_URL}/projects/${projectId}/personas`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

/**
 * Get a single persona by ID
 */
export async function getPersona(projectId: string, personaId: string, token: string): Promise<Persona> {
  const response = await axios.get(
    `${API_BASE_URL}/projects/${projectId}/personas/${personaId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

/**
 * Create a new persona
 */
export async function createPersona(projectId: string, input: CreatePersonaInput, token: string): Promise<Persona> {
  const response = await axios.post(
    `${API_BASE_URL}/projects/${projectId}/personas`,
    input,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

/**
 * Update a persona
 */
export async function updatePersona(projectId: string, personaId: string, input: UpdatePersonaInput, token: string): Promise<Persona> {
  const response = await axios.put(
    `${API_BASE_URL}/projects/${projectId}/personas/${personaId}`,
    input,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

/**
 * Delete a persona
 */
export async function deletePersona(projectId: string, personaId: string, token: string): Promise<void> {
  await axios.delete(
    `${API_BASE_URL}/projects/${projectId}/personas/${personaId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
}

/**
 * Generated persona data from AI
 */
export interface GeneratedPersonaData {
  name: string;
  role: string;
  description: string;
  goals: string;
  pain_points: string;
  age_range: string;
  gender: string;
  location: string;
}

/**
 * Response from AI persona generation
 */
export interface GeneratePersonaResponse {
  success: boolean;
  persona: GeneratedPersonaData;
  metadata: {
    tokensUsed: number;
    model: string;
  };
}

/**
 * Generate a persona using AI based on project requirements
 */
export async function generatePersonaWithAI(projectId: string, token: string): Promise<GeneratePersonaResponse> {
  const response = await axios.post(
    `${API_BASE_URL}/projects/${projectId}/personas/generate`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
}

/**
 * Available age range options
 */
export const AGE_RANGE_OPTIONS = [
  '0-18',
  '19-29',
  '30-39',
  '40-49',
  '50-59',
  '60-69',
  '70+'
];

/**
 * Available gender options
 */
export const GENDER_OPTIONS = [
  'Man',
  'Woman',
  'I\'d Prefer Not To Say'
];

/**
 * Available avatar options
 */
export const AVATAR_OPTIONS = [
  { id: 'avatar-1', label: 'Avatar 1', imagePath: '/avatars/avatar-1.png' },
  { id: 'avatar-2', label: 'Avatar 2', imagePath: '/avatars/avatar-2.png' },
  { id: 'avatar-3', label: 'Avatar 3', imagePath: '/avatars/avatar-3.png' },
  { id: 'avatar-4', label: 'Avatar 4', imagePath: '/avatars/avatar-4.png' },
  { id: 'avatar-5', label: 'Avatar 5', imagePath: '/avatars/avatar-5.png' },
  { id: 'avatar-6', label: 'Avatar 6', imagePath: '/avatars/avatar-6.png' },
  { id: 'avatar-7', label: 'Avatar 7', imagePath: '/avatars/avatar-7.png' },
  { id: 'avatar-8', label: 'Avatar 8', imagePath: '/avatars/avatar-8.png' },
  { id: 'avatar-9', label: 'Avatar 9', imagePath: '/avatars/avatar-9.png' },
  { id: 'avatar-10', label: 'Avatar 10', imagePath: '/avatars/avatar-10.png' }
];

/**
 * Get avatar image path for a given avatar ID
 */
export function getAvatarImagePath(avatarId: string): string {
  const avatar = AVATAR_OPTIONS.find(a => a.id === avatarId);
  return avatar?.imagePath || '/avatars/avatar-1.png';
}
